import { useState } from 'react';
import { FolderTree, Pencil, Plus, Trash2, Users } from 'lucide-react';
import {
  useListGroupsWithStaffQuery,
  useCreateGroupMutation,
  useUpdateGroupMutation,
  useDeleteGroupMutation,
  getApiError,
  type Group,
} from '@/store/api';
import { useAuthStore } from '@/store/auth.store';
import { PageLayout } from '@/components/ui/PageLayout';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Button } from '@/components/ui/Button';
import { Field, Input } from '@/components/ui/Field';
import { Mark } from '@/components/ui/Badge';
import { GroupStaffDialog } from './GroupStaffDialog';
import { TriangleAlert } from 'lucide-react';

type Draft = { _id?: string; name: string; description: string };

const EMPTY: Draft = { name: '', description: '' };

/*
  Groups — classes, year groups, streams.

  The create and edit forms used to be inline panels that pushed the list down
  as they opened, with a tinted `bg-brand-50` ground that existed nowhere else
  in the product. They are now one dialog in two modes, which keeps the list
  still while you work and means create and edit cannot drift apart in wording
  or validation.
*/
export function GroupsPage() {
  const institutionId = useAuthStore((s) => s.user?.institutionId ?? '');

  const [draft, setDraft] = useState<Draft | null>(null);
  const [formError, setFormError] = useState('');
  const [pendingDelete, setPendingDelete] = useState<Group | null>(null);

  const [staffFor, setStaffFor] = useState<string | null>(null);

  const { data: groups = [], isLoading, isError, error } = useListGroupsWithStaffQuery(institutionId, {
    skip: !institutionId,
  });
  const [createGroup, createMutation] = useCreateGroupMutation();
  const [updateGroup, updateMutation] = useUpdateGroupMutation();
  const [deleteGroup, deleteMutation] = useDeleteGroupMutation();

  const isEditing = Boolean(draft?._id);
  const saving = createMutation.isLoading || updateMutation.isLoading;

  const closeForm = () => {
    setDraft(null);
    setFormError('');
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft?.name.trim()) return;
    setFormError('');

    const data = {
      name: draft.name.trim(),
      description: draft.description.trim() || undefined,
    };

    try {
      if (draft._id) {
        await updateGroup({ institutionId, groupId: draft._id, data }).unwrap();
      } else {
        await createGroup({ institutionId, data: { ...data, order: groups.length } }).unwrap();
      }
      closeForm();
    } catch (err) {
      setFormError(getApiError(err));
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteGroup({ institutionId, groupId: pendingDelete._id }).unwrap();
      setPendingDelete(null);
    } catch {
      /* Left open so the failure is visible next to the thing that failed. */
    }
  };

  /* The row shape the richer listing returns — the class, its roll and who
     teaches it. */
  type GroupRow = (typeof groups)[number];

  const columns: Column<GroupRow>[] = [
    {
      key: 'name',
      header: 'Group',
      render: (g) => (
        <div className="flex items-center gap-2.5">
          <span
            className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md border border-info/25 bg-info/[0.08] text-[hsl(var(--record-ink-info))]"
            aria-hidden
          >
            <FolderTree size={12} />
          </span>
          <span className="font-semibold text-foreground">{g.name}</span>
        </div>
      ),
    },
    {
      key: 'description',
      header: 'Description',
      render: (g) => (
        <span className="text-muted-foreground">{g.description || <span className="opacity-50">—</span>}</span>
      ),
      className: 'max-sm:hidden',
    },
    {
      key: 'students',
      header: 'Students',
      align: 'num',
      render: (g) => <span className="tabular-nums text-foreground">{g.studentCount}</span>,
      className: 'w-[100px]',
    },
    {
      key: 'staff',
      header: 'Teachers',
      render: (g) =>
        g.staff.length === 0 ? (
          /* Not an error, but worth seeing: a class with nobody on it has
             nobody who can take its register or enter its marks. */
          <Mark tone="warning">Nobody yet</Mark>
        ) : (
          <span className="truncate text-muted-foreground">
            {g.staff.map((s) => s.fullName).join(', ')}
          </span>
        ),
      className: 'w-[260px]',
    },
    {
      key: 'actions',
      header: '',
      align: 'num',
      render: (g) => (
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setStaffFor(g._id)}
            title="Who can take this class's register and enter its marks"
          >
            <Users size={12} className="mr-1" />
            Teachers
          </Button>
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            aria-label={`Edit ${g.name}`}
            icon={<Pencil size={12} />}
            onClick={() => setDraft({ _id: g._id, name: g.name, description: g.description ?? '' })}
          />
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            aria-label={`Remove ${g.name}`}
            icon={<Trash2 size={12} />}
            className="text-[hsl(var(--record-ink-danger))] hover:bg-destructive/10"
            onClick={() => setPendingDelete(g)}
          />
        </div>
      ),
      className: 'w-[90px]',
    },
  ];

  return (
    <PageLayout
      title="Groups"
      subtitle="Classes, year groups, or any grouping that fits your institution"
      breadcrumbs={[{ label: 'Register' }, { label: 'Groups' }]}
      meta={
        groups.length > 0 ? (
          <span className="text-[11px] tabular-nums text-muted-foreground">
            {groups.length} group{groups.length === 1 ? '' : 's'}
          </span>
        ) : undefined
      }
      actions={
        <Button variant="primary" icon={<Plus size={13} />} onClick={() => setDraft(EMPTY)}>
          New group
        </Button>
      }
    >
      <DataTable
        columns={columns}
        rows={groups}
        rowKey={(g) => g._id}
        loading={isLoading}
        error={isError || undefined}
        errorMessage={isError ? getApiError(error) : undefined}
        emptyTitle="No groups yet"
        emptyDescription="Create groups for your classes or year groups. Students are assigned to one when they are enrolled."
        emptyAction={
          <Button variant="primary" icon={<Plus size={13} />} onClick={() => setDraft(EMPTY)}>
            Create the first group
          </Button>
        }
      />

      <Modal
        open={draft !== null}
        onClose={closeForm}
        title={isEditing ? 'Edit group' : 'New group'}
        description={
          isEditing
            ? 'Renaming a group does not move any student out of it.'
            : 'Use the name the school itself uses — it appears on the gate app.'
        }
        footer={
          <>
            <Button variant="secondary" onClick={closeForm} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              form="group-form"
              loading={saving}
              disabled={!draft?.name.trim()}
            >
              {isEditing ? 'Save changes' : 'Create group'}
            </Button>
          </>
        }
      >
        {draft && (
          <form id="group-form" onSubmit={handleSave} className="flex flex-col gap-3" noValidate>
            <Field label="Name" htmlFor="group-name" required>
              <Input
                id="group-name"
                autoFocus
                placeholder="Class 4A, JHS 1, Nursery B"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </Field>
            <Field label="Description" htmlFor="group-desc" hint="Optional.">
              <Input
                id="group-desc"
                placeholder="Fourth grade, stream A"
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              />
            </Field>
            {formError && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-md border border-destructive/25 bg-destructive/[0.06] px-2.5 py-2 text-[11px] font-medium text-[hsl(var(--record-ink-danger))]"
              >
                <TriangleAlert size={13} className="mt-[1px] flex-shrink-0" />
                {formError}
              </div>
            )}
          </form>
        )}
      </Modal>

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => void handleDelete()}
        title={`Remove ${pendingDelete?.name ?? 'group'}?`}
        confirmLabel="Remove group"
        loading={deleteMutation.isLoading}
        body={
          <>
            Students in this group will become ungrouped. They stay on the register and keep their
            pickup codes — only the grouping is removed.
            {deleteMutation.error && (
              <p className="mt-2 font-medium text-[hsl(var(--record-ink-danger))]">
                {getApiError(deleteMutation.error)}
              </p>
            )}
          </>
        }
      />

      {/* Looked up from the live list, not held as a snapshot — saving
          refreshes it underneath and a copy would go stale. */}
      <GroupStaffDialog
        open={staffFor !== null}
        onClose={() => setStaffFor(null)}
        institutionId={institutionId}
        group={groups.find((g) => g._id === staffFor) ?? null}
      />

    </PageLayout>
  );
}
